import moment from 'moment';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DateRangePicker } from 'react-date-range';
import { useSelector } from 'react-redux';
import { Button, Card, IconButton, Row, Typography } from '@tempmee/react';
import { OutlineXIcon } from '@tempmee/react-icons';
import { DebouncedSearchField } from '../DebouncedSearchField';
import { FiltersCard } from '../FiltersCard';
import { DsoManagerFilter } from './DsoManagerFilter';
import { DsoNameFilter } from './DsoNameFilter';
import { DsoRAMFilter } from './DsoRamFilter';
import { RamFilter } from './RamFilter';
import { SelectFilter } from './SelectFilter';
import { StateFilter } from './StateFilter';
import { useAvailableSpecialties } from '../../../hooks/useAvailableSpecialties';
import { EducationUtils } from '../../../utils/EducationUtils';
import { UserSubProfession } from '../../../enums/UserProfession';

// Status and Worker Type send their "all" sentinel raw; the specialty selects
// map it to '' — the fetch actions already speak those dialects.
const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'open', label: 'Open' },
  { value: 'counter', label: 'Counter' },
  { value: 'filled', label: 'Filled' },
  { value: 'pay-out', label: 'Pay Out' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'cancelled-hyg', label: 'Cancelled by HYG' },
  { value: 'cancelled-dnt', label: 'Cancelled by DNT' },
  { value: 'expired', label: 'Expired' },
  { value: 'complete', label: 'Complete' },
  { value: 'refill', label: 'Refill' },
  { value: 'adjusted-required', label: 'Adjusted required' },
  { value: 'pay-out-disabled', label: 'Payout Disabled' },
  { value: 'refund-disabled', label: 'Refund Disabled' },
  { value: 'pending-counter', label: 'Pending & Counter' },
];

const WORKER_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'employee', label: 'Employee' },
  { value: 'contractor', label: 'Contractor' },
];

// The applied range arrives as Dates from the picker and as ISO strings from
// a hydrated filter, depending on the page.
const DATE_LIKE = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.instanceOf(Date),
]);

const withAllOption = (values, toLabel) => [
  { value: 'all', label: 'All' },
  ...values.map((value) => ({ value, label: toLabel(value) })),
];

/**
 * The filter fields shared by the job lists — Temp Shifts and Interviews — for
 * ListPage's `filters` card.
 *
 * The two pages had grown separate 450-line copies of the same eleven fields,
 * differing only in where the applied date range is read from and whether
 * clearing it also pokes Redux. Those are the two props; everything else is
 * identical, so it lives here once.
 */
export const JobFilters = ({
  filter,
  handleFilterChange,
  payOutOnly,
  appliedRange,
  onDateRangeCleared,
}) => {
  const allProfessionsAndSubProfessions = useSelector(
    (state) => state.jobs.allProfessionsAndSubProfessions,
  );
  const requestedEducations = useSelector((state) => state.users.requestedEducations);
  const [showDateRange, setShowDateRange] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  });

  const availableRdhSpecialties = useAvailableSpecialties(requestedEducations?.RDH);
  const availableDaSpecialties = useAvailableSpecialties(requestedEducations?.DA);

  const toggleDateRange = () => setShowDateRange(!showDateRange);

  const clearDateRangeSearch = () => {
    setDateRange({ startDate: new Date(), endDate: new Date(), key: 'selection' });
    handleFilterChange('dateRange', { startDate: null, endDate: null, key: 'selection' });
    onDateRangeCleared?.();
  };

  const professionOptions = [
    { value: 'all', label: 'All' },
    ...allProfessionsAndSubProfessions.map((profession) => ({
      value: profession.code,
      label: profession.name,
    })),
  ];

  // The picker keeps its show/hide transition from the pre-DS version.
  const pickerStyle = showDateRange
    ? {
        visibility: 'visible',
        transition: 'height 0.5s, opacity 0.7s linear',
        height: 450,
        opacity: 1,
      }
    : {
        visibility: 'hidden',
        height: 0,
        // Card pads with p-2; zero it here too or the collapsed box still
        // holds its padding open as an empty white strip.
        padding: 0,
        opacity: 0,
        transition: 'height 0.5s, opacity 0.3s linear, visibility 0.3s',
      };

  return (
    <>
      <DebouncedSearchField
        label="Search"
        value={filter.searchText}
        onChange={(value) => handleFilterChange('searchText', value)}
      />

      {!payOutOnly ? (
        <SelectFilter
          label="Status"
          options={STATUS_OPTIONS}
          value={filter.jobStatus}
          onChange={(jobStatus) => handleFilterChange('jobStatus', jobStatus)}
        />
      ) : null}

      <StateFilter filter={filter} handleFilterChange={handleFilterChange} />

      <SelectFilter
        label="Profession"
        options={professionOptions}
        value={filter.subProfession || filter.profession || 'all'}
        onChange={(optionValue) => {
          if (optionValue === UserSubProfession.EFDA) {
            handleFilterChange('subProfession', optionValue);
          } else {
            handleFilterChange('profession', optionValue !== 'all' ? optionValue : '');
            handleFilterChange('subProfession', '');
          }
        }}
      />

      <RamFilter filter={filter} handleFilterChange={handleFilterChange} />

      <DsoNameFilter filter={filter} handleFilterChange={handleFilterChange} />

      <DsoManagerFilter filter={filter} handleFilterChange={handleFilterChange} />

      <DsoRAMFilter filter={filter} handleFilterChange={handleFilterChange} />

      <SelectFilter
        label="RDH Skills"
        options={withAllOption(availableRdhSpecialties, EducationUtils.removeSpecialtyPrefix)}
        value={filter.specialtyRdh || 'all'}
        onChange={(specialty) =>
          handleFilterChange('specialtyRdh', specialty !== 'all' ? specialty : '')}
      />

      <SelectFilter
        label="DA Skills"
        options={withAllOption(availableDaSpecialties, EducationUtils.removeSpecialtyPrefix)}
        value={filter.specialtyDa || 'all'}
        onChange={(specialty) =>
          handleFilterChange('specialtyDa', specialty !== 'all' ? specialty : '')}
      />

      <SelectFilter
        label="Worker Type"
        options={WORKER_TYPE_OPTIONS}
        value={filter.workerClassification || 'all'}
        onChange={(workerClassification) =>
          handleFilterChange('workerClassification', workerClassification)}
      />

      <Row align="end">
        {/* `font-medium` for the same reason ListPageHeader corrects its
            actions: the DS hardcodes font-bold, Figma's button token is Medium
            500, and 700 on a borderless button reads far heavier than the mock.
            tailwind-merge means this replaces the DS class rather than fighting
            it. Delete when the DS emits the Figma weight. */}
        <Button
          variant="text"
          color="secondary"
          size="small"
          className="font-medium"
          onClick={toggleDateRange}
        >
          Search by Date Range
        </Button>
      </Row>

      {appliedRange?.startDate ? (
        <FiltersCard.FullRow>
          <Row align="center" justify="end" gap={2}>
            <Typography variant="body2" className="text-text-secondary">
              Selected Date Range:{' '}
              {`${moment(appliedRange.startDate).format('MM-DD-YYYY') || ''} - ${
                moment(appliedRange.endDate).format('MM-DD-YYYY') || ''
              }`}
            </Typography>
            <IconButton
              aria-label="Clear date range"
              appearance="default"
              Icon={<OutlineXIcon />}
              onClick={clearDateRangeSearch}
            />
          </Row>
        </FiltersCard.FullRow>
      ) : null}

      <FiltersCard.FullRow>
        {/* min-h-0 + overflow-hidden so the collapsed state is truly 0 tall —
            Card's own min-h-20 would otherwise hold an 80px blank row open. */}
        <Card className="min-h-0 overflow-hidden rounded-lg" style={pickerStyle}>
          <DateRangePicker
            ranges={[dateRange]}
            onChange={(value) => setDateRange(value.selection)}
          />
          <Row justify="center" py={2}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => {
                handleFilterChange('dateRange', dateRange);
                toggleDateRange();
              }}
            >
              Search
            </Button>
          </Row>
        </Card>
      </FiltersCard.FullRow>
    </>
  );
};

JobFilters.propTypes = {
  filter: PropTypes.shape({}).isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  /** Pay Out Jobs has no status filter — the status is the page. */
  payOutOnly: PropTypes.bool,
  /** The range currently applied to the list, for the readout. */
  appliedRange: PropTypes.shape({
    startDate: DATE_LIKE,
    endDate: DATE_LIKE,
  }),
  /** Extra side effects after clearing — Shifts also has to poke Redux. */
  onDateRangeCleared: PropTypes.func,
};

JobFilters.defaultProps = {
  payOutOnly: false,
  appliedRange: undefined,
  onDateRangeCleared: undefined,
};

export default JobFilters;
